/** Shared view contracts. Mutations and generation belong to the authenticated Gambit service. */
import type { StudioLibrary } from "./studioLibrary";
import type {
  StudioImageQuality,
  StudioImageSettings,
} from "./studioImageQuality";
import { StudioCharacter, blueprintStartingPoint } from "./studioCharacters";
import type { StudioChatMode } from "./studioChatMode";
import type { StudioChatSettings } from "./studioChatModels";
import type { VideoScenePlan } from "./studioVideoPlan";
import { StudioChoices, describeStudioChoices } from "./studioChoices";
// Retain the old key only for historical artifacts and saved conversations.
export type Stage = "blueprint" | "script" | "audio" | "world" | "artwork" | "video" | "finish" | "storyboard";
export const STAGES: readonly Stage[] = [
  "blueprint",
  "script",
  "audio",
  "world",
  "artwork",
  "video",
  "finish",
] as const;
export const stageLabels: Record<Stage, string> = {
  blueprint: "Blueprint",
  script: "Script",
  world: "Cast & world",
  audio: "Audio",
  storyboard: "Storyboard",
  artwork: "Storyboard",
  video: "Video",
  finish: "Summary",
};
export type StudioMedia = {
  visualReview?: { status: 'passed' | 'needs-attention' | 'unavailable'; repaired?: boolean;
    issues?: { evidence: string; correction: string }[] };
  dialogueSegments?: { text: string; voiceId: string; speaker?: string }[];
  id: string;
  title: string;
  mime: string;
  url: string;
  referenceId?: string;
  duration?: number;
  role?: string;
  kind?: string;
  start?: number;
  voiceTakeId?: string;
  audioMode?: StudioChoices["audioMode"];
};
export type StudioReferenceUpload = { name: string; mime: string; base64: string };
export type Artifact = {
  followUp?: string;
  scriptDocument?: import("./studioScript").ScriptDocument;
  previousImages?: StudioMedia[];
  media?: StudioMedia[];
  locationViews?: StudioMedia[];
  data?: Record<string, any>;
  duration?: number;
  id: string;
  stage: Stage;
  title: string;
  text: string;
  detail: string;
  inputIds: string[];
  count?: number;
  prompt?: string;
  imageSettings?: StudioImageSettings;
  characters?: StudioCharacter[];
  videoScenes?: VideoScenePlan[];
  choices?: StudioChoices;
  audioMix?: string[];
  audioExperience?: StudioChoices["audioMode"];
  audioExperienceOptions?: { mode: StudioChoices["audioMode"]; title: string }[];
  audioRevision?: { sourceId: string; layers: ("voice" | "effects" | "ambience" | "music" | "song")[] };
  audioPlan?: {
    regenerate?: "all" | "voice" | "effects" | "ambience" | "music" | "sound";
    musicPrompt?: string;
    scoreSections?: { anchor: string | null; placement: "under" | "after" | null; musicEnabled: boolean; direction: string }[];
    segments: { text: string; speaker: string; voiceId: string }[];
    soundDesign: { kind?: "sound" | "ambience"; prompt: string; start: number; duration: number | null; anchor?: string | null; placement?: "before" | "under" | "after" | null }[];
  };
};
export type TimedScript = { id: string; audioId: string; duration: number; instrumental?: boolean; cues: { id: string; start: number; end: number; text: string; words?: { text: string; start: number; end: number }[] }[] };
export type VideoConnection = { transition: "hard-cut" | "continuous-action" | "camera-move"; joinNext: boolean; endAtNext: boolean; direction: string };
export type VideoClip = { id: string; title: string; start: number; end: number; sceneIds: string[]; startSceneId: string; endSceneId?: string };
export type StoryboardScene = { videoConnection?: VideoConnection; id: string; title: string; start: number; end: number; description: string; imagePrompt: string; referenceIds: string[]; transcriptText?: string };
export type Sequence = {
  localStory?: { artifactId: string; mediaId: string; jobId: string };
  publishedStory?: { storyId: string; sourceId: string; userId: string; jobId: string };
  worldPreparation?: "working" | "ready";
  storyboardPreparation?: "working" | "ready";
  timedScript?: TimedScript;
  storyboardGeneration?: { cost: number; count: number; inputFingerprint: string; readyIds: string[] };
  worldGeneration?: { cost: number; count: number; inputFingerprint: string; readyIds: string[] };
  audioSettings?: Partial<StudioChoices>;
  audioChoices?: StudioChoices;
  audioGeneration?: { cost: number; inputFingerprint: string };
  audioScriptSourceId?: string;
  id: string;
  name: string;
  note: string;
  parentId?: string;
  artifacts: Partial<Record<Stage, Artifact>>;
  locked: Stage[];
  videoSkipped?: boolean;
};
export type Quote = {
  clips?: VideoClip[];
  followUp?: string;
  audioLayers?: string[];
  tool?: { kind: string; name: string; prompt: string; previewText: string };
  local?: boolean;
  pricing?: string;
  estimatedProviderUsd?: number;
  providerUsdPerSecond?: number;
  providerSeconds?: number;
  tokensPerSecond?: number;
  count?: number;
  id: string;
  sequenceId: string;
  stage: Stage;
  cost: number;
  status: "quoted" | "running" | "complete" | "cancelled";
  candidateId?: string;
  prompt?: string;
  imageSettings?: StudioImageSettings;
  characters?: StudioCharacter[];
  videoScenes?: VideoScenePlan[];
  choices?: StudioChoices;
  direction?: string;
};
export type Message = {
  followUp?: string;
  delivery?: "sending" | "unconfirmed" | "failed";
  cursor?: number;
  displayText?: string;
  characterOffers?: StudioCharacterOffer[];
  parts?: StudioDocumentPart[];
  responseType?: "reply" | "questions" | "selection" | "draft";
  questions?: StudioQuestion[];
  assets?: StudioMedia[];
  id: string;
  role: "user" | "assistant";
  text: string;
  sequenceId: string;
  quote?: Quote;
  artifactStage?: Stage;
  stage?: Stage;
  characters?: StudioCharacter[];
  chatMode?: StudioChatMode;
  chatSettings?: StudioChatSettings;
  event?: string;
  jobId?: string;
  request?: StudioJob;
  workUpdates?: StudioWorkUpdate[];
  workSummary?: StudioWorkSummary;
};
export type StudioDocumentPart = {
  type: "document";
  id: string;
  operation: "review" | "create" | "revise" | "alternative" | "attach_notes";
  stage: "blueprint" | "script";
  artifactId: string;
  sequenceId: string;
  sourceId?: string;
  artifact?: Artifact;
  source?: Artifact;
  notes?: string;
  notesStage?: "blueprint" | "script";
  noteId?: string;
};
export type StudioQuestion = { id: string; title: string; options: string[]; multiple?: boolean };
export type StudioCharacterOffer = { name: string; description: string; voiceDescription?: string; appearanceOptions?: string[]; voiceOptions?: string[] };
export type StudioCharacterDesign = { name: string; characterId: string; voiceId?: string; selectedVoiceId?: string; status: "requested" | "accepted" | "failed" };
export type StudioWorkUpdate = { id: string; text: string; kind: "summary" | "phase" | "action"; createdAt: string };
export type StudioWorkSummary = { durationMs?: number; milestones: StudioWorkUpdate[]; status?: string };
export type StudioJob = {
  quoteId?: string;
  completedVideos?: StudioMedia[];
  completedImages?: StudioMedia[];
  id: string;
  chapterId: string;
  sequenceId: string;
  stage: Stage;
  status: string;
  mode?: string;
  progress?: string;
  createdAt?: string;
  completedAt?: string;
  progressVersion?: number;
  workUpdates?: StudioWorkUpdate[];
  workSummary?: StudioWorkSummary;
  acknowledgement?: string;
  waitingUpdate?: { elapsedSeconds: number; updatedAt: string } | null;
  generationId?: string;
  cancelRequestedAt?: string;
  retriedBy?: string;
  interruptedBy?: string;
  error?: string;
  failureKind?: 'storage' | 'insufficient-tokens' | 'image-policy';
  tokenShortfall?: { available: number; required: number; reason?: 'model-minimum' };
  canRetry?: boolean;
  retryBlockedReason?: string;
};
export type Conversation = { id: string; title: string; messages: Message[] };
export type SavedStoryContinuation = { storyId: string; chapterIds: string[]; sourceHash: string };
export type Chapter = {
  /** Browser-only placeholder; omitted from the confirmed workspace cache. */
  localOnly?: boolean;
  chapterNumber?: number;
  continuation?: SavedStoryContinuation;
  autoTitle?: boolean;
  assistantAssets?: StudioMedia[];
  jobs?: {
    id: string;
    mode?: string;
    stage: Stage;
    status: string;
    progress: string;
    acknowledgement?: string;
    cancelRequestedAt?: string;
  }[];
  id: string;
  title: string;
  sequences: Sequence[];
  activeSequenceId: string;
  conversations: Conversation[];
  activeConversationId: string;
  prompts?: Partial<Record<Stage, string>>;
  imageQuality?: StudioImageQuality;
  chatMode?: StudioChatMode;
  chatSettings?: StudioChatSettings;
  blueprintCharacters?: StudioCharacter[];
  stageChatSettings?: Partial<Record<Stage, StudioChatSettings>>;
  characterSetup?: { mode?: "automatic" | "custom"; designs?: Record<string, StudioCharacterDesign> };
  characterFollowUp?: string;
  characterOffers?: StudioCharacterOffer[];
  stageChoices?: Partial<Record<Stage, Partial<StudioChoices>>>;
  scriptNotes?: { sourceId?: string; jobId?: string; text: string };
  documentNotes?: Record<string, { id: string; sourceId: string; stage: "blueprint" | "script"; text: string }>;
};
export type Project = {
  continuation?: SavedStoryContinuation;
  /** Display-only source hint until the server verifies story ownership and context. */
  continuationSource?: { storyId: string; chapterCount?: number };
  updatedAt?: string;
  lastMessageCursor?: number;
  autoTitle?: boolean;
  starter?: boolean;
  id: string;
  title: string;
  chapters: Chapter[];
  pinned?: boolean;
};
export type StudioTreeTarget = { projectId: string; chapterId?: string };
export const getTreeItem = (state: StudioState, target: StudioTreeTarget) => {
  const project = state.projects.find((p) => p.id === target.projectId);
  return target.chapterId
    ? project?.chapters.find((c) => c.id === target.chapterId)
    : project;
};
export type StudioState = {
  schema: 1;
  projects: Project[];
  projectId: string;
  chapterId: string;
  balance: number;
};
export type Context = {
  projectId: string;
  chapterId: string;
  conversationId: string;
};
export type StudioAction =
  | { type: "link-story"; sequenceId?: string; stage: "artwork"; artifactId: string; storyId: string; sourceId: string; userId: string; jobId: string }
  | { type: "new-project"; title?: string; id?: string }
  | { type: "stop-job" | "retry-job"; jobId: string }
  | { type: "rename"; target: StudioTreeTarget; title: string }
  | {
      type: "pin";
      target: { projectId: string; chapterId?: never };
      pinned: boolean;
    }
  | { type: "delete"; target: StudioTreeTarget }
  | {
      type: "navigate";
      projectId: string;
      chapterId: string;
      conversationId?: string;
    }
  | { type: "select"; sequenceId: string; stage?: Stage }
  | { type: "prompt"; stage: Stage; text: string }
  | { type: "choices"; stage: Stage; choices: Partial<StudioChoices> }
  | {
      type: "quote-choices";
      context: Context;
      quoteId: string;
      choices: Partial<StudioChoices>;
    }
  | { type: "blueprint-characters"; characters: StudioCharacter[]; append?: boolean; context?: Context }
  | { type: "resolve-character-offers"; sourceId: string; library: StudioLibrary; context: Context }
  | { type: "character-setup-choice"; mode: "automatic" | "custom"; context: Context }
  | { type: "character-design-state"; design: StudioCharacterDesign; context: Context }
  | { type: "chat-mode"; mode: StudioChatMode }
  | { type: "chat-settings"; stage?: Stage; settings: StudioChatSettings }
  | { type: "lock"; stage: Stage; artifactId?: string; sequenceId?: string }
  | { type: "skip-video" }
  | { type: "message"; stage: Stage; text: string; id: string; interruptJobId?: string; intent?: "suggest-blueprint"; displayText?: string; characters?: StudioCharacter[]; referenceImages?: StudioReferenceUpload[] }
  | { type: "quote"; stage: Stage; id: string }
  | { type: "prepare-audio"; stage: "audio"; id: string }
  | { type: "prepare-timed-script" | "prepare-storyboard"; stage: "artwork"; id: string }
  | { type: "edit-timed-cue"; stage: "artwork"; transcriptId: string; cueId: string; text: string }
  | { type: "edit-scene-direction"; stage: "artwork"; artifactId: string; sceneId: string; text: string }
  | { type: "keep-scene-image"; stage: "artwork"; artifactId: string; sceneId: string }
  | { type: "retry-scene"; stage: "artwork"; artifactId: string; sceneId: string }
  | { type: "storyboard-quality"; stage: "artwork"; quality: StudioImageQuality }
  | { type: "generate-scenes"; sequenceId?: string; artifactId?: string; stage: "artwork"; id: string; expectedCost: number; inputFingerprint: string }
  | { type: "assemble-story"; sequenceId?: string; artifactId?: string; stage: "artwork"; id: string; inputFingerprint: string }
  | { type: "prepare-world"; stage: "world"; id: string }
  | { type: "generate-world"; sequenceId?: string; artifactId?: string; stage: "world"; id: string; expectedCost: number; inputFingerprint: string }
  | { type: "world-quality"; stage: "world"; quality: StudioImageQuality }
  | { type: "retry-world-reference"; stage: "world"; artifactId: string; referenceId: string }
  | { type: "generate-audio"; sequenceId?: string; artifactId?: string; stage: "audio"; id: string; expectedCost: number; inputFingerprint: string }
  | { type: "audio-mix"; artifactId: string; selected: string[] }
  | { type: "audio-experience"; artifactId: string; experience: StudioChoices["audioMode"] }
  | { type: "generate-script"; id: string }
  | { type: "clear-script-notes" }
  | { type: "clear-document-notes"; sourceId: string; noteId: string }
  | {
      type: "image-quality";
      context: Context;
      quoteId: string;
      quality: StudioImageQuality;
    }
  | {
      type: "approve" | "complete" | "cancel";
      context: Context;
      quoteId: string;
    }
  | { type: "edit"; stage: Stage; text: string; id: string }
  | { type: "edit-script-segment"; stage?: "audio"; sequenceId: string; artifactId: string; segmentIndex: number;
      text: string }
  | {
      type: "new-conversation";
      title: string;
      id: string;
    }
  | {
      type: "continue-saved-story";
      storyId: string;
      title?: string;
      chapterCount?: number;
    }
  | {
      type: "new-chapter";
      title?: string;
      id: string;
      projectId?: string;
    };

export const getActive = (state: StudioState) => {
  const project = state.projects.find((p) => p.id === state.projectId)!;
  const chapter = project.chapters.find((c) => c.id === state.chapterId)!;
  return {
    project,
    chapter,
    sequence: chapter.sequences.find((s) => s.id === chapter.activeSequenceId)!,
    conversation: chapter.conversations.find(
      (c) => c.id === chapter.activeConversationId,
    )!,
  };
};

export const missingPrerequisite = (
  sequence: Sequence,
  stage: Stage,
): Stage | undefined => {
  // Storybooks are complete with artwork and audio. Video is an optional addition.
  const required = STAGES.slice(0, STAGES.indexOf(stage)).filter(
    (s) => s !== "video",
  );
  return required.find((s) => !sequence.locked.includes(s));
};

export const isStoryReady = (sequence: Sequence) =>
  !missingPrerequisite(sequence, "finish");

export const canAddChapter = (project: Project) => {
  if (!project.chapters.length) return true;
  const latest = project.chapters.at(-1);
  const chosen = latest?.sequences.find(
    (s) => s.id === latest.activeSequenceId,
  );
  return !!chosen && isStoryReady(chosen);
};

export const isStageLocked = (sequence: Sequence, stage: Stage) =>
  stage === "finish" ? isStoryReady(sequence) : sequence.locked.includes(stage);

export const canVisitStage = (sequence: Sequence, stage: Stage) =>
  isStageLocked(sequence, stage) || !missingPrerequisite(sequence, stage);

export const currentStage = (sequence: Sequence): Stage =>
  missingPrerequisite(sequence, "video") ??
  (sequence.videoSkipped ||
  sequence.locked.includes("video") ||
  sequence.locked.includes("finish")
    ? "finish"
    : "video");

export const hasRunningStage = (chapter: Chapter, stage: Stage) =>
  !!chapter.jobs?.some(
    (job) =>
      job.status === "running" && (!["prepare-world-ahead", "prepare-storyboard-ahead"].includes(job.mode || "") || stage === job.stage) && (stage === "finish" || job.stage === stage),
  ) ||
  chapter.conversations.some((t) =>
    t.messages.some(
      (m) =>
        m.quote?.status === "running" &&
        (stage === "finish" || m.quote.stage === stage),
    ),
  );

export const getTreeDeletionBlockReason = (
  state: StudioState,
  target: StudioTreeTarget,
): string | undefined => {
  const project = state.projects.find((p) => p.id === target.projectId);
  const item = getTreeItem(state, target);
  if (!project || !item) return "This item is no longer available.";
  const chapters = project.chapters;
  if (chapters.some((chapter) => chapter.jobs?.some(job => job.status === "running") || hasRunningStage(chapter, "finish")))
    return "Wait for generation to finish before deleting.";
};

export const canDeleteTreeItem = (
  state: StudioState,
  target: StudioTreeTarget,
) => !getTreeDeletionBlockReason(state, target);

const preservesLocks = (current: Sequence, candidate: Sequence) =>
  current.locked
    .filter((stage) => stage !== "finish")
    .every(
      (stage) =>
        current.artifacts[stage]?.id === candidate.artifacts[stage]?.id,
    );

export const canApproveQuote = (chapter: Chapter, quote: Quote) => {
  const current = chapter.sequences.find(
    (s) => s.id === chapter.activeSequenceId,
  )!;
  const source = chapter.sequences.find((s) => s.id === quote.sequenceId);
  return (
    !!source &&
    (!quote.local ||
      ((source.id === current.id || (quote.stage === "video" && !quote.tool)) &&
        !chapter.jobs?.some((job) => job.status === "running" && !["prepare-world-ahead", "prepare-storyboard-ahead"].includes(job.mode || "")))) &&
    quote.stage !== "finish" &&
    !(
      quote.stage === "script" &&
      (hasRunningStage(chapter, "script") || source.id !== current.id)
    ) &&
    !isStageLocked(current, quote.stage) &&
    !missingPrerequisite(current, quote.stage) &&
    preservesLocks(current, source)
  );
};

export const canUseTake = (chapter: Chapter, candidateId: string, stage?: Stage) => {
  const current = chapter.sequences.find(
    (s) => s.id === chapter.activeSequenceId,
  )!;
  const candidate = chapter.sequences.find((s) => s.id === candidateId);
  return (
    !!candidate &&
    candidate.id !== current.id &&
    !chapter.jobs?.some((job) => job.status === "running" && !["prepare-world-ahead", "prepare-storyboard-ahead"].includes(job.mode || "")) &&
    (preservesLocks(current, candidate) || (stage === "audio" &&
      current.locked.includes("script") && candidate.locked.includes("script") &&
      !current.locked.some(locked => !["blueprint", "script"].includes(locked)) &&
      !current.publishedStory && !current.localStory &&
      !!current.artifacts.blueprint && current.artifacts.blueprint.id === candidate.artifacts.blueprint?.id &&
      !!candidate.artifacts.script &&
      (current.audioScriptSourceId || current.artifacts.script?.id) ===
        (candidate.audioScriptSourceId || candidate.artifacts.script?.id)))
  );
};

export const stageInputStages: Record<Stage, Stage[]> = {
  blueprint: [],
  script: ["blueprint"],
  world: ["blueprint", "script", "audio"],
  audio: ["blueprint", "script"],
  storyboard: ["blueprint", "script", "audio", "world"],
  artwork: ["blueprint", "script", "audio", "world"],
  video: ["blueprint", "script", "audio", "world", "artwork"],
  finish: [
    "blueprint",
    "script",
    "audio",
    "world",
    "artwork",
    "video",
  ],
};

/** The summary is derived from chosen, locked artifacts, never another generation. */
export function buildStorySummary(sequence: Sequence, title: string): Artifact {
  const artifacts = STAGES.filter(
    (stage) => stage !== "finish" && sequence.locked.includes(stage),
  ).flatMap((stage) =>
    sequence.artifacts[stage] ? [sequence.artifacts[stage]!] : [],
  );
  const withVideo =
    sequence.locked.includes("video") && !!sequence.artifacts.video;
  return {
    id: `summary-${sequence.id}`,
    stage: "finish",
    title,
    detail: withVideo
      ? "Storybook with audio and video"
      : "Storybook with audio",
    inputIds: artifacts.map((a) => a.id),
    text: [
      title,
      withVideo
        ? "Storybook with audio and video"
        : "Storybook with audio · video can be added later",
      ...artifacts.map(
        (a) => `${stageLabels[a.stage].toUpperCase()}\n${a.detail}\n${a.text}`,
      ),
    ].join("\n\n"),
  };
}

export const getStageInputs = (sequence: Sequence, stage: Stage): Artifact[] =>
  stageInputStages[stage].flatMap((input) =>
    sequence.locked.includes(input) && sequence.artifacts[input]
      ? [sequence.artifacts[input]!]
      : [],
  );

export function buildStagePrompt(sequence: Sequence, stage: Stage): string {
  if (stage === "blueprint" && !sequence.artifacts.blueprint)
    return blueprintStartingPoint;
  const storyMode = sequence.artifacts.blueprint?.choices?.storyMode;
  const audioDirection =
    storyMode === "Musical"
      ? "Shape the song’s mood and performance. Choose lyrics or instrumental audio, then describe the pace and feeling you want."
      : storyMode === "Dialogue"
        ? "Choose a narrator and voices for your cast. Tell me how the conversations should sound, and where to add atmosphere or music."
        : "Choose a narrator and audio experience. Describe the tone, pace and atmosphere you’d like.";
  const direction =
    stage === "script"
      ? sequence.artifacts.script
        ? `Revise the current script:\n${sequence.artifacts.script.text}`
        : "Generate a script from your locked blueprint, or refine the story direction in chat first."
      : sequence.artifacts[stage]?.prompt ||
        (stage === "audio" && storyMode
          ? audioDirection
          : `Prepare the ${stageLabels[stage].toLowerCase()} from your approved story choices.`);
  if (stage !== "script" && sequence.artifacts[stage]?.prompt) return direction;
  const sources = getStageInputs(sequence, stage)
    .map(
      (a) =>
        `${stageLabels[a.stage]} (locked):\n${a.text}${a.choices ? `\n${describeStudioChoices(a.stage, a.choices)}` : ""}`,
    )
    .join("\n\n");
  return [direction, sources && `Build from these story choices:\n${sources}`]
    .filter(Boolean)
    .join("\n\n");
}
